/**
 * 數值格式化。只做本套件需要的兩件事：亞分金額、比率。
 *
 * 為什麼不重用 `@tpte/ui` 的 `formatMoney`：它吃 **整數分**，把 0.0003 美元
 * （0.03 分）四捨五入成 0 分 → `$0.00`。可觀測性面板的成本幾乎全在亞分區間，
 * 顯示成 0 等於看不出成本，所以另立一個吃 **美元浮點** 的格式化器。
 * 負號、幣別符號、locale 的行為刻意與 `formatMoney` 一致（U+2212、Intl 取符號）。
 */

/** 幣別：面板成本只會用到這幾種，符號交給 Intl 推導。 */
export type MicroCostCurrency = "USD" | "TWD" | "EUR" | "JPY";

export interface MicroCostFormatOptions {
  /** 每 N 次的金額。`1000` = 每千次（會把值 ×1000 並接上 suffix）。 */
  per?: 1 | 1000;
  currency?: MicroCostCurrency;
  locale?: string;
  /** 是否帶幣別符號，預設 `true`（成本一定要看得出幣別）。 */
  showCurrency?: boolean;
  /** 強制小數位（2–6）。不給就依數量級自動挑，最少 2 位、最多 6 位。 */
  decimals?: number;
  /** 每千次時的後綴，預設 `" /1K"`。 */
  perSuffix?: string;
}

const DEFAULT_LOCALE = "en-US";
const MINUS = "\u2212";
const MIN_DECIMALS = 2;
const MAX_DECIMALS = 6;

const symbolCache = new Map<string, string>();

function currencySymbol(currency: MicroCostCurrency, locale: string): string {
  const key = `${locale}|${currency}`;
  const cached = symbolCache.get(key);
  if (cached !== undefined) return cached;
  let symbol: string = currency;
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol"
    }).formatToParts(0);
    symbol = parts.find((part) => part.type === "currency")?.value ?? currency;
  } catch {
    symbol = currency;
  }
  symbolCache.set(key, symbol);
  return symbol;
}

/**
 * 自動小數位：留 2 位有效數字，夾在 2–6 之間。
 * 0.3 → 2、0.012 → 3、0.0003 → 6（夾住上限）。
 */
function autoDecimals(value: number): number {
  const abs = Math.abs(value);
  if (!(abs > 0)) return MIN_DECIMALS;
  const exponent = Math.floor(Math.log10(abs));
  return Math.min(MAX_DECIMALS, Math.max(MIN_DECIMALS, MIN_DECIMALS - exponent));
}

/** 去掉多餘尾零，但至少留 2 位小數（金額要看得出「有兩位」）。 */
function trimDecimals(text: string): string {
  const dot = text.indexOf(".");
  if (dot < 0) return `${text}.00`;
  const head = text.slice(0, dot);
  const fraction = text.slice(dot + 1).replace(/0+$/, "");
  return `${head}.${fraction.padEnd(MIN_DECIMALS, "0")}`;
}

/**
 * 亞分金額：美元浮點 → 自適應小數字串。
 *
 * - `0.0003` → `US$0.0003`、`0.012` → `US$0.012`、`1.5` → `US$1.50`
 * - `per: 1000` 先乘 1000 再接後綴：`0.0003` → `US$0.30 /1K`
 * - 負號是 U+2212，與 `formatMoney` 一致
 * - `0` 回 `US$0.00`；NaN／Infinity／null 一律丟 TypeError
 *   （成本是帳務數字，缺值靜靜顯示 0 會被當成真的 0 元）
 */
export function formatMicroCost(usd: number, options: MicroCostFormatOptions = {}): string {
  if (typeof usd !== "number" || !Number.isFinite(usd)) {
    throw new TypeError(`formatMicroCost 需要有限的數字（美元），收到：${String(usd)}`);
  }

  const {
    per = 1,
    currency = "USD",
    locale = DEFAULT_LOCALE,
    showCurrency = true,
    decimals,
    perSuffix = " /1K"
  } = options;

  const scaled = usd * per;
  const negative = usd < 0;
  const places = decimals ?? autoDecimals(scaled);
  const clamped = Math.min(MAX_DECIMALS, Math.max(MIN_DECIMALS, places));
  const body = trimDecimals(Math.abs(scaled).toFixed(clamped));

  const symbol = showCurrency ? currencySymbol(currency, locale) : "";
  const sign = negative ? MINUS : "";
  const suffix = per === 1000 ? perSuffix : "";

  return `${sign}${symbol}${body}${suffix}`;
}

/** 比率 0–1 → `12.3%`。缺值回破折號（不假裝 0%）。 */
export function formatShare(share: number | null | undefined, decimals = 1): string {
  if (share === null || share === undefined || !Number.isFinite(share)) return "—";
  const pct = share * 100;
  return `${pct.toFixed(decimals).replace(/\.0+$/, "")}%`;
}

/** 延遲／次數的緊湊寫法：1234 → `1,234`，小數最多 2 位。 */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
